import { useEffect, useState } from 'react';
import { GridCellParams } from '@mui/x-data-grid-pro';
import Chip from '@mui/material/Chip';
import { IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { Link as LinkRecord, LinkAllocation, fetchLink } from '../../../store/Link';
import { DetailViewCard } from '../../Compounds/CardWithTitle';
import { addManualOverride, getLinkAllocationByLinkId, removeManualOverride } from '../../../Api/Link/LinkApi';
import { BasicDataGrid } from '../../Compounds/Table/BasicDataGrid';
import { GridCustomColDef } from '../../types';
import { chipStyles, formatPercent, formatPrice, getFormattedDate } from '../../../utils/helpers';
import { DEFAULT_MINI_TABLE_SX_STYLES } from '../../../utils/styleHelpers';
import { TextBlock, TextSpan } from '../../Atoms/Text';
import { getSelectedTab, updateState } from '../../../store/GeneralStore';
import { ConfirmationModal } from '../../Atoms';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { RepeatingOverrideModal } from './RepeatingOverrideModal';
import { Icon } from '../../Atoms/Icon';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { getCurrentUser, UserRole } from '../../../store/User';
import { RecordLink } from '../../Atoms/RecordLink';

interface LinkAllocationsParams {
  link: LinkRecord;
  fromDrawer?: boolean;
}

const LinkAllocationColDefs: GridCustomColDef[] = [
  {
    field: 'rpmRunDate',
    headerName: 'Run',
    headerAlign: 'left',
    align: 'center',
    flex: 0.75,
    width: 100,
    type: 'singleSelect',
    valueOptions: [],
    sortable: false,
    filterable: false,
    renderCell: (params: GridCellParams) => {
      const date = params.value as string;
      if (!date) return '';
      return getFormattedDate(date, 'MMM YY');
    }
  },
  {
    field: 'runStatus',
    headerName: 'Run Status',
    headerAlign: 'left',
    align: 'center',
    flex: 1,
    width: 100,
    type: 'singleSelect',
    valueOptions: [],
    sortable: false,
    filterable: false,
    renderCell: (params: GridCellParams) => {
      const status = params.value as string;
      if (!status) return '';
      return (
        <Chip
          label={status}
          size="small"
          sx={chipStyles(status)}
          title={status}
        />
      );
    }
  },
  {
    field: 'allocationPercent',
    headerName: 'Allocation',
    headerAlign: 'left',
    align: 'center',
    flex: 0.75,
    width: 100,
    type: 'singleSelect',
    valueOptions: [],
    sortable: false,
    filterable: false,
    renderCell: (params: GridCellParams<LinkAllocation>) => {
      const className = params.row.isManualOverride ? 'bg-dark-orange text-white' : 'bg-gray-100 text-black';
      return <span className={`py-1 px-2 rounded-md ${className}`}>{formatPercent(params.value as number, true)}</span>;
    }
  },
  {
    field: 'chargeRecurringAllocated',
    headerName: 'Charge',
    headerAlign: 'left',
    align: 'right',
    flex: 1,
    width: 100,
    type: 'singleSelect',
    valueOptions: [],
    sortable: false,
    filterable: false,
    valueFormatter: (value) => formatPrice(value),
    renderCell: (params: GridCellParams) => (
      <span className={params.value && Number(params.value) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    field: 'revenueRecurringAllocated',
    headerName: 'Revenue',
    headerAlign: 'left',
    align: 'right',
    flex: 1,
    width: 100,
    type: 'singleSelect',
    valueOptions: [],
    sortable: false,
    filterable: false,
    valueFormatter: (value) => formatPrice(value),
    renderCell: (params: GridCellParams) => (
      <span className={params.value && Number(params.value) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  }
];

export const LinkAllocationSection = ({ link, fromDrawer = false }: LinkAllocationsParams) => {
  const [records, setRecords] = useState<LinkAllocation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showRepeatingOverride, setShowRepeatingOverride] = useState(false);
  const [showDeleting, setShowDeleting] = useState(false);
  const { setSnack } = useSnackbar();
  const currentUserRole = getCurrentUser()?.role;
  const canEdit = currentUserRole && [UserRole.ADMIN, UserRole.ENGINEER, UserRole.ANALYST].includes(currentUserRole);

  const fetchAllocations = async () => {
    setRecords([]);
    setIsLoading(true);
    if (!link.id) return;
    const { data: allocations } = await getLinkAllocationByLinkId(link.id);
    if (allocations) setRecords(allocations);
    setIsLoading(false);
  };
  useEffect(() => {
    fetchAllocations();
  }, [link.id]);

  const sxStyle = {
    ...DEFAULT_MINI_TABLE_SX_STYLES,
    minHeight: 'inherit',
    height: 'inherit',
    border: 'none',
    borderRadius: '0',
    '.MuiDataGrid-columnHeader': {
      ...DEFAULT_MINI_TABLE_SX_STYLES['.MuiDataGrid-columnHeader'],
      color: '#000000',
      backgroundColor: '#F7F7F7',
      fontWeight: 600
    },
    '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell, .MuiDataGrid-overlay': {
      borderRight: '1px solid #E0E0E0'
    },
    '& .MuiDataGrid-columnsContainer': {
      borderBottom: '1px solid #E0E0E0'
    },
    '.MuiDataGrid-columnSeparator': {
      display: 'none'
    },
    '.MuiDataGrid-main': {
      border: '1px solid #E0E0E0',
      borderRight: 'none'
    }
  };

  const handleDetailClick =
    (type: 'assetDetail' | 'provisionalAssetDetail' | 'rpmAccountDetail', tabIndex: number) => () => {
      updateState({
        selectedTab: {
          ...getSelectedTab(),
          [type]: tabIndex
        }
      });
    };

  const enabledRepeatingOverride = link.repeatingOverrideEnabled && !!link.repeatingOverridePercent;
  const repeatingOverrideModifiedName =
    link.repeatingOverrideModifiedBy && link.repeatingOverrideModifiedBy.firstName
      ? `${link.repeatingOverrideModifiedBy.firstName} ${link.repeatingOverrideModifiedBy.lastName}`
      : '';

  const handleSaveRepeatingOverride = async (value: number) => {
    const overrideData = {
      overridePercent: value
    };
    const { error } = await addManualOverride(link.id as string, overrideData);
    if (error) {
      setSnack({ message: error.message, type: 'error', open: true });
    } else {
      fetchLink(link.id as string, fromDrawer);
      fetchAllocations();
      setShowRepeatingOverride(false);
      return true;
    }
  };

  const handleDelete = async () => {
    setShowDeleting(true);
    const { error } = await removeManualOverride(link.id as string);
    if (error) {
      setSnack({ message: error.message, type: 'error', open: true });
    } else {
      fetchLink(link.id as string, fromDrawer);
      fetchAllocations();
    }
    setShowDeleting(false);
  };

  const cardActions = () => {
    if (enabledRepeatingOverride || !canEdit) return undefined;
    return (
      <button
        className="flex items-center space-x-2"
        onClick={() => setShowRepeatingOverride(true)}
        data-cy="repeating-override-button"
        data-testid="repeating-override-button">
        <Icon type="plus" />
        <TextSpan
          color="indigo"
          size="sm14">
          Repeating Override
        </TextSpan>
      </button>
    );
  };

  return (
    <DetailViewCard
      enableCollapse
      key="allocation"
      title="Allocation"
      dataTestId="allocation-view-card"
      cardActions={cardActions()}>
      <div className="flex px-4 py-4 w-full flex-col">
        {isLoading ? (
          <ComponentSpinner label="Loading Allocation Data" />
        ) : (
          <BasicDataGrid
            rows={records}
            columns={LinkAllocationColDefs}
            sxStyle={sxStyle}
            disableColumnResize
            disableColumnMenu
            className="flex w-full"
            dataTestId="allocation-link-data-grid"
          />
        )}
        {records.length > 0 && (
          <div className="flex justify-center flex-row mt-6">
            <div className="w-1/2 pl-2">
              <TextBlock
                size="sm14"
                color="grey6"
                dataTestId="text-block">
                {link.assetSfId ? 'Asset' : 'Provisional Asset'}
              </TextBlock>
              <RecordLink
                className="text-indigo text-[14px]"
                onClick={handleDetailClick(link.assetSfId ? 'assetDetail' : 'provisionalAssetDetail', 2)}
                to={`/${link.assetSfId ? 'asset' : 'provisional-asset'}/${link.assetId}`}>
                <div className="flex items-center">Allocation History</div>
              </RecordLink>
            </div>
            <div className="w-1/2 pl-2">
              <TextBlock
                size="sm14"
                color="grey6"
                dataTestId="text-block">
                RPM Account
              </TextBlock>
              <RecordLink
                className="text-indigo text-[14px]"
                onClick={handleDetailClick('rpmAccountDetail', 1)}
                to={`/rpm-account/${link.rpmAccountId}`}>
                <div className="flex items-center">Allocation History</div>
              </RecordLink>
            </div>
          </div>
        )}
        {enabledRepeatingOverride && canEdit && (
          <div className="flex justify-center flex-row mt-4 border-t -mx-4 px-4">
            <div className="w-1/2 pl-2">
              <div className="mb-1 pr-9 pt-4">
                <TextBlock
                  size="sm14"
                  color="grey6">
                  Repeating Override
                </TextBlock>
                {showDeleting ? (
                  <ComponentSpinner label="Deleting..." />
                ) : (
                  <TextBlock
                    size="sm14"
                    color="white"
                    className="flex space-x-2 items-center">
                    <span className="bg-dark-orange py-1 px-2 rounded-md">
                      {formatPercent(link.repeatingOverridePercent, true)}
                    </span>
                    <span className="space-x-0 flex items-center">
                      <IconButton
                        sx={{
                          color: 'rgb(107 114 128)'
                        }}
                        size="small"
                        title="Edit"
                        onClick={() => setShowRepeatingOverride(true)}>
                        <EditNoteIcon />
                      </IconButton>
                      <IconButton
                        sx={{
                          color: 'rgb(107 114 128)'
                        }}
                        size="small"
                        title="Delete"
                        onClick={() => setShowConfirm(true)}>
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </TextBlock>
                )}
              </div>
            </div>
            <div className="w-1/2 pl-2">
              <div className="mb-1 pr-9 pt-4">
                <TextBlock
                  size="sm14"
                  color="grey6">
                  Modified at
                </TextBlock>
                <TextBlock
                  size="sm14"
                  color={link.repeatingOverrideModifiedAt ? 'grey9' : 'grey3'}>
                  {link.repeatingOverrideModifiedAt
                    ? getFormattedDate(link.repeatingOverrideModifiedAt, 'lll')
                    : 'None'}
                </TextBlock>
              </div>
              <div className="mb-1 pr-9 pt-4">
                <TextBlock
                  size="sm14"
                  color="grey6">
                  Modified by
                </TextBlock>
                <TextBlock
                  size="sm14"
                  color={repeatingOverrideModifiedName ? 'grey9' : 'grey3'}>
                  {repeatingOverrideModifiedName ? repeatingOverrideModifiedName : 'None'}
                </TextBlock>
              </div>
            </div>
            <ConfirmationModal
              open={showConfirm}
              setOpen={setShowConfirm}
              handleSubmit={handleDelete}
              text={'Are you sure you want to delete this Repeating Override?'}
              confirmButtonText={'Delete'}
              title={'Delete Confirmation'}
            />
          </div>
        )}
        {canEdit && (
          <RepeatingOverrideModal
            value={link.repeatingOverridePercent ?? undefined}
            open={showRepeatingOverride}
            setOpen={setShowRepeatingOverride}
            handleSubmit={handleSaveRepeatingOverride}
          />
        )}
      </div>
    </DetailViewCard>
  );
};
